import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import _get from 'lodash/get';
import useFilterParams from '@lib/hooks/useFilterParams';
import { GetUrlParams } from '@lib/enums/urls';
import { GraphQLFormattedError } from 'graphql';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationCreateInputObject,
  DocumentationUpdateInputObject,
  Tag,
  TaskScopeNameEnum,
  useEdmDocumentationCreateMutation,
  useEdmDocumentationUpdateMutation,
  useEdmDocumentationCategoriesLazyQuery,
  useTagCreateMutation,
  useTagsLazyQuery,
  useSectorsSearchListLazyQuery,
  useSitesSearchListLazyQuery,
  useUsersSearchListLazyQuery,
  useAssetsSearchListLazyQuery,
  useSiteAreasSearchListLazyQuery,
  useFormTemplatesSearchListLazyQuery,
  useTasksSearchListLazyQuery,
} from 'graphql-common';
import useEdmDocumentationQueryHook from 'utils/fetch/hooks/useEdmDocumentationQueryHook';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { Values } from '@lib/interfaces/form';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';
import { getDocumentationCategoriesLoadOptions } from 'utils/fetch/searchLists/documentationCategoriesSearchList';
import { getTagsLoadOptions } from 'utils/fetch/searchLists/tagsSearchList';
import { getSectorsLoadOptions } from 'utils/fetch/searchLists/sectorsSearchList';
import { getSitesLoadOptions } from 'utils/fetch/searchLists/sitesSearchList';
import { getUsersLoadOptions } from 'utils/fetch/searchLists/usersSearchList';
import { getAssetsLoadOptions } from 'utils/fetch/searchLists/assetsSearchList';
import { getSiteAreasLoadOptions } from 'utils/fetch/searchLists/siteAreasSearchList';
import { getFormTemplatesLoadOptions } from 'utils/fetch/searchLists/formTemplatesSearchList';
import { getTasksLoadOptions } from 'utils/fetch/searchLists/tasksSearchList';
import getPreparedDataFromValues from './getPreparedDataFromValues';
import getDefaultValuesFromQueryData from './getDefaultValuesFromQueryData';
import useDocumentLinkedEntityIds from './useDocumentLinkedEntityIds';
import createTaskGroupKeyResolver from '../../utils/createTaskGroupKeyResolver';

export default function useDocumentFormActions({
  isEdit,
  isPlanLocked,
  hasCategoriesAccess,
  hasSitesAccess,
  hasAssetsAccess,
  hasTasksAccess,
  hasUsersAccess,
}: {
  isEdit?: boolean;
  isPlanLocked?: boolean;
  hasCategoriesAccess?: boolean;
  hasSitesAccess?: boolean;
  hasAssetsAccess?: boolean;
  hasTasksAccess?: boolean;
  hasUsersAccess?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [filterParams] = useFilterParams();
  const apolloClient = useApolloClient();
  const getTaskGroupKey = useMemo(
    () => createTaskGroupKeyResolver(apolloClient),
    [apolloClient],
  );

  const assetIdParam = filterParams[GetUrlParams.AssetId] as string | undefined;
  const assetNameParam = filterParams[GetUrlParams.AssetName] as
    | string
    | undefined;

  const {
    data,
    loading: dataLoading,
    firstLoading: dataFirstLoading,
  } = useEdmDocumentationQueryHook({
    id,
    isViewLocked: isPlanLocked,
    skip: !isEdit,
  });

  const {
    sectorIds,
    siteIds,
    assetIds,
    areaIds,
    formTemplateIds,
    taskIds,
    loading: linkedLoading,
    firstLoading: linkedFirstLoading,
  } = useDocumentLinkedEntityIds({
    documentId: id,
    // BE keeps `documentationsId` filter gated on sectors / sites /
    // siteAreas / formTemplates / tasks, but `assets(documentationsId:)` is
    // open on Free plan. The hook splits the gating internally — the asset
    // query runs in both modes (so the edit form can prefill the linked
    // equipment) while the other five are skipped when `isPlanLocked`.
    isPlanLocked,
    skip: !isEdit,
  });

  const linkedEntityIds = useMemo(
    () => ({
      sectorIds,
      siteIds,
      assetIds,
      areaIds,
      formTemplateIds,
      taskIds,
    }),
    [sectorIds, siteIds, assetIds, areaIds, formTemplateIds, taskIds],
  );

  const firstLoading = dataFirstLoading || linkedFirstLoading;

  const prefillData = useMemo(() => {
    if (!assetIdParam) return undefined;
    return { assetId: assetIdParam, assetName: assetNameParam };
  }, [assetIdParam, assetNameParam]);

  const [createMutation, createMutationResult] =
    useEdmDocumentationCreateMutation();
  const [updateMutation, updateMutationResult] =
    useEdmDocumentationUpdateMutation();
  const [tagCreateMutation] = useTagCreateMutation();

  const [fetchCategories] = useEdmDocumentationCategoriesLazyQuery({
    fetchPolicy: 'network-only',
  });
  const categoriesLoadOptions = useCallback(
    () => getDocumentationCategoriesLoadOptions(fetchCategories),
    [fetchCategories],
  );

  const [fetchTags] = useTagsLazyQuery({ fetchPolicy: 'network-only' });
  const tagsLoadOptions = useCallback(
    () => getTagsLoadOptions(fetchTags),
    [fetchTags],
  );

  const [fetchSectors] = useSectorsSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const sectorsLoadOptions = useCallback(
    () => getSectorsLoadOptions(fetchSectors),
    [fetchSectors],
  );

  const [fetchSites] = useSitesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const sitesLoadOptions = useCallback(
    () => getSitesLoadOptions(fetchSites, undefined, { includeSector: true }),
    [fetchSites],
  );

  const [fetchUsers] = useUsersSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const approverLoadOptions = useCallback(
    (name, values) => {
      const categoryId = _get<string>(
        values,
        [DocumentFormFieldNames.Category, 'value'],
        '',
      );
      return getUsersLoadOptions(fetchUsers, {
        custom: {
          byCore: {
            onlyActive: true,
            access: {
              component: AccessComponentEnum.DocumentationManagement,
              level: AccessLevelEnum.Write,
              sourceableId: categoryId?.length ? categoryId : undefined,
            },
          },
        },
      });
    },
    [fetchUsers],
  );

  const [fetchAssets] = useAssetsSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const equipmentLoadOptions = useCallback(
    () => getAssetsLoadOptions(fetchAssets),
    [fetchAssets],
  );

  const [fetchSiteAreas] = useSiteAreasSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const areasLoadOptions = useCallback(
    () => getSiteAreasLoadOptions(fetchSiteAreas),
    [fetchSiteAreas],
  );

  const [fetchFormTemplates] = useFormTemplatesSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const formTemplatesLoadOptions = useCallback(
    () => getFormTemplatesLoadOptions(fetchFormTemplates),
    [fetchFormTemplates],
  );

  const [fetchTasks] = useTasksSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const tasksLoadOptions = useCallback(
    () =>
      getTasksLoadOptions({
        fetchLazyQuery: fetchTasks,
        scope: { name: TaskScopeNameEnum.Active },
        selectedSiteIds: [],
      }),
    [fetchTasks],
  );

  const onCreateTag = (
    name: string,
    callback: (tag: Tag | null) => void,
    errorCallback: (errors: readonly GraphQLFormattedError[]) => void,
  ) => {
    tagCreateMutation({
      variables: { attributes: { name } },
      onCompleted: (res) => {
        toast({ content: t('create-tag-success') });
        callback(res?.data as Tag);
      },
      onError: (error) => {
        const reason = _get(error, [
          'graphQLErrors',
          0,
          'extensions',
          'details',
          0,
          'meta',
          'tag',
          'reason',
        ]);
        if (reason !== 'taken') {
          showGraphQLErrors(error, t);
        }
        errorCallback(error.graphQLErrors);
      },
    });
  };

  const onSubmitForm = (values: Values) => {
    const prevValues = getDefaultValuesFromQueryData(
      data,
      undefined,
      linkedEntityIds,
    );
    const prepared = getPreparedDataFromValues(
      values,
      prevValues,
      isEdit,
      isPlanLocked,
      {
        hasCategoriesAccess,
        hasSitesAccess,
        hasAssetsAccess,
        hasTasksAccess,
        hasUsersAccess,
      },
      getTaskGroupKey,
    );

    if (isEdit) {
      updateMutation({
        variables: {
          id,
          attributes: prepared as DocumentationUpdateInputObject,
          isViewLocked: isPlanLocked,
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          navigate(
            APP_URLS.dashboard.documentation.view.getDynamicPath({
              pathParts: { id },
            }),
          );
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    } else {
      createMutation({
        variables: {
          attributes: prepared as DocumentationCreateInputObject,
          isViewLocked: isPlanLocked,
        },
        onCompleted: (completedData) => {
          toast({ content: t('create-document-success') });
          if (assetIdParam) {
            navigate(
              APP_URLS.dashboard.assets.documentation.getDynamicPath({
                pathParts: { id: assetIdParam },
              }),
            );
          } else {
            const newId = completedData?.data?.id;
            if (newId) {
              navigate(
                APP_URLS.dashboard.documentation.view.getDynamicPath({
                  pathParts: { id: newId },
                }),
              );
            } else {
              navigate(
                APP_URLS.dashboard.documentation.index.getPathWithQuery(),
              );
            }
          }
        },
        onError: (error) => showGraphQLErrors(error, t),
      });
    }
  };

  const onCancel = () => {
    if (isEdit && id) {
      navigate(
        APP_URLS.dashboard.documentation.view.getDynamicPath({
          pathParts: { id },
        }),
      );
    } else if (assetIdParam) {
      navigate(
        APP_URLS.dashboard.assets.documentation.getDynamicPath({
          pathParts: { id: assetIdParam },
        }),
      );
    } else {
      navigate(APP_URLS.dashboard.documentation.index.getPathWithQuery());
    }
  };

  const loading =
    dataLoading ||
    linkedLoading ||
    createMutationResult.loading ||
    updateMutationResult.loading;
  const graphQLErrors =
    createMutationResult?.error?.graphQLErrors ||
    updateMutationResult?.error?.graphQLErrors;

  return {
    approverLoadOptions,
    areasLoadOptions,
    categoriesLoadOptions,
    data,
    equipmentLoadOptions,
    firstLoading,
    formTemplatesLoadOptions,
    graphQLErrors,
    linkedEntityIds,
    loading,
    onCancel,
    onCreateTag,
    onSubmitForm,
    prefillData,
    sectorsLoadOptions,
    sitesLoadOptions,
    tagsLoadOptions,
    tasksLoadOptions,
  };
}
